import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { verifyEmailRequestSchema } from "@nh/shared";
import { Alert, AuthCard, AuthLayout, Button, PulseLoader } from "@/shared/ui/core";
import { useRunOnce } from "@/shared/lib/useRunOnce";
import { verifyEmailRequest } from "./auth.api";
import { emailLinkErrorMessage } from "./auth.errors";
import { AuthSwitchLink } from "./components/AuthSwitchLink";
import { ResendVerification } from "./components/ResendVerification";

/**
 * Cuando la URL no trae token, o trae uno que ni siquiera tiene forma de
 * token. Aquí el servidor todavía no ha hablado, así que lo único que se
 * puede afirmar es que el enlace está incompleto — nada de "expiró".
 */
const BROKEN_LINK_MESSAGE =
  "Este enlace no trae la llave que hace falta. Puede que se haya cortado " +
  "al copiarlo del correo. Pide uno nuevo aquí abajo.";

type Phase =
  | { kind: "working" }
  | { kind: "verified"; email: string }
  | { kind: "failed"; message: string };

/**
 * `/verificar?token=…` — el aterrizaje del enlace de verificación.
 *
 * ── El canje corre UNA vez ──────────────────────────────────────────
 * Y por eso usa `useRunOnce`. Este patrón —arrancar algo asíncrono al
 * montar— se escribió a mano dos veces en este proyecto y las dos salió
 * roto igual: un candado `useRef` MÁS una bandera `alive` apagada por el
 * cleanup, que en StrictMode se anulan entre sí y dejan la pantalla
 * cargando para siempre. `useRunOnce` es el candado sin la mitad que
 * sobra; lee su comentario antes de "arreglar" nada aquí.
 *
 * Que corra una sola vez importa MÁS aquí que en el callback de Google:
 * un token de verificación se consume al canjearlo. Un segundo canje del
 * mismo token no es una llamada de más, es una llamada que el servidor
 * rechaza — así que la pantalla enseñaría "este enlace no funciona"
 * justo después de haber verificado el correo con éxito.
 *
 * ── Verificar no es entrar ──────────────────────────────────────────
 * El éxito no deja sesión: deja un botón para ir a entrar, con el correo
 * ya puesto. Quien abre el enlace suele hacerlo en el teléfono mientras
 * la cuenta se creó en la computadora, y dar sesión aquí sería dársela
 * al dispositivo equivocado.
 */
export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>({ kind: "working" });

  useRunOnce(() => {
    const token = searchParams.get("token") ?? "";

    // La forma del token se la pregunta al contrato —el mismo esquema que
    // valida el servidor— en vez de contar caracteres a mano aquí.
    if (!verifyEmailRequestSchema.shape.token.safeParse(token).success) {
      setPhase({ kind: "failed", message: BROKEN_LINK_MESSAGE });
      return;
    }

    verifyEmailRequest({ token })
      .then(({ email }) => setPhase({ kind: "verified", email }))
      .catch((error: unknown) => {
        // TAL CUAL lo que dijo el servidor. Responde lo mismo si el enlace
        // caducó, si nunca existió o si ya se usó, así que aquí no se sabe
        // cuál fue y no se puede adornar con un "parece que expiró".
        setPhase({ kind: "failed", message: emailLinkErrorMessage(error) });
      });
  });

  return (
    <AuthLayout width="sm">
      <AuthCard
        title={TITLES[phase.kind]}
        subtitle={phase.kind === "working" ? "Es solo un momento." : undefined}
        footer={
          /*
           * El pie SOLO en el fallo.
           *
           * Mientras trabaja no hay nada que ofrecer todavía. Y en el
           * éxito sería absurdo: acaba de verificar su cuenta, así que
           * "¿No tienes una cuenta?" es la única pregunta cuya respuesta
           * ya conocemos — y competiría con el botón que sí le sirve.
           *
           * En el fallo sí: quien abrió un enlace muerto puede ser alguien
           * que nunca terminó de registrarse.
           */
          phase.kind === "failed" ? (
            <AuthSwitchLink
              prompt="¿No tienes una cuenta?"
              linkLabel="Crea una"
              to="/register"
            />
          ) : undefined
        }
      >
        {phase.kind === "working" && (
          <PulseLoader label="Verificando tu correo…" className="py-4" />
        )}

        {phase.kind === "verified" && (
          <div className="grid gap-4">
            <Alert variant="success">
              Ya verificamos{" "}
              <span className="font-medium">{phase.email}</span>. Tu cuenta está
              lista.
            </Alert>
            <Button
              variant="primary"
              size="lg"
              shape="rounded"
              fullWidth
              // El correo viaja en el estado de navegación y no en la URL:
              // el login lo usa para prellenar el campo (que es para lo que
              // el contrato lo devuelve) sin dejarlo escrito en el
              // historial del navegador de un dispositivo que puede ser
              // prestado.
              onClick={() =>
                navigate("/login", { replace: true, state: { email: phase.email } })
              }
            >
              Ir a inicio de sesión
            </Button>
          </div>
        )}

        {phase.kind === "failed" && (
          <div className="grid gap-4">
            <Alert variant="error">{phase.message}</Alert>
            {/* La salida está AQUÍ y no en otra pantalla: quien llegó con
                un enlace muerto ya hizo su parte, y mandarlo a buscar
                dónde pedir otro es perderlo. */}
            <ResendVerification />
          </div>
        )}
      </AuthCard>
    </AuthLayout>
  );
}

const TITLES: Record<Phase["kind"], string> = {
  working: "Verificando tu correo…",
  verified: "¡Listo, correo verificado!",
  // Neutro: el servidor responde igual si el enlace caducó, si nunca
  // existió o si ya se usó, así que "expiró" sería mentira a menudo.
  failed: "Ese enlace no funciona",
};
